import React from 'react';
import { graphql, StaticQuery } from 'gatsby';

import Layout from '../components/layout';
import SEO, { siteUrl, personId, websiteId } from '../components/seo';
import MailtoLink from '../components/MailtoLink';

import '../utils/normalize.css';
import '../utils/css/screen.css';

const site = require('./../../siteConfig');

const experiences = [
  {
    date: '2026 — Now',
    role: 'Inference Platform Engineer',
    org: 'Lyceum · Zurich, Switzerland',
    sub: 'Dedicated Inference for Open Models.'
  },
  {
    date: '2023 — 2026',
    role: 'Software Engineer',
    org: 'CERN · CMS Experiment',
    sub:
      'End-to-end web systems for physics data workflows on the Worldwide LHC Computing Grid. React, FastAPI, Kubernetes/ArgoCD, Oracle/Mongo/MySQL. 2024 CMS Award recipient.'
  },
  {
    date: '2021 — 2023',
    role: 'Research Engineer (CV & ML)',
    org: 'Retrocausal · Redmond, WA (Remote)',
    sub:
      'Activity recognition & action segmentation for the factory floor. PyQt + Django + VueJS on AWS. +10% accuracy on flagship model. Published at ECCV 2024 and ISMAR 2022.'
  },
  {
    date: 'Summer 2021',
    role: 'OpenLab Summer Student',
    org: 'CERN · BioDynaMo',
    sub:
      'CI/CD automation for the agent-based simulation platform — Docker + GitHub Actions. Refreshed publishing workflows from Binder to JupyterLab.'
  },
  {
    date: '2021',
    role: 'Junior Data Scientist',
    org: 'LiberaTrade · Karachi, Pakistan',
    sub:
      'Time-series forecasting models in R and Python, integrated into a supply-chain operations MVP deployed on AWS.'
  },
  {
    date: '2019 — 2020',
    role: 'Teaching Assistant',
    org: 'FAST-NUCES · Computer Organization & Assembly',
    sub: 'Led lab sessions and graded assignments for a class of 60+ students.'
  },
  {
    date: '2017 — 2021',
    role: 'BS Computer Science',
    org: 'FAST-NUCES · Karachi, Pakistan',
    sub:
      '3.78/4.00 CGPA, Magna Cum Laude. Rector\u2019s List (Fall 2020, Spring 2021), Dean\u2019s List ×5.'
  }
];

const metrics = [
  { val: 'CMS', label: 'Award 2024' },
  { val: 'ECCV', label: 'Paper · 2024' },
  { val: 'ISMAR', label: 'Paper · 2022' },
  { val: '3.78', label: 'CGPA · Magna Cum Laude' },
  { val: 'EPFL', label: 'Argusa ’25 · Runner-up' },
  { val: 'CKAD', label: 'Certified K8s Dev' }
];

const publications = [
  {
    label: 'Learning by Aligning 2D Skeleton Sequences and Multi-Modality Fusion',
    venue: 'ECCV 2024',
    href: 'https://arxiv.org/abs/2305.19480',
    type: 'ScholarlyArticle',
    year: '2024'
  },
  {
    label: 'AI-mediated Job Status Tracking in AR as a No-Code Service',
    venue: 'ISMAR-Adjunct 2022',
    href: 'https://doi.org/10.1109/ISMAR-Adjunct57072.2022.00203',
    type: 'ScholarlyArticle',
    year: '2022'
  },
  {
    label: 'BioDynaMo Lab',
    venue: 'Zenodo 2021',
    href: 'https://doi.org/10.5281/zenodo.5727687',
    type: 'CreativeWork',
    year: '2021'
  }
];

const selectedWork = [
  {
    title: 'P&R Dashboard',
    subtitle: 'CERN · CMS Award 2024',
    body:
      'Operational control plane for CMS production and reprocessing. React + FastAPI, CERN SSO, audit logging, workflow monitoring.',
    image: '/work/pnr-dashboard.png'
  },
  {
    title: 'Archi',
    subtitle: 'MIT · Open Source',
    body:
      'LLM-powered conversational AI for operational intelligence. Document ingestion, semantic search, and retrieval-augmented answers.',
    image: '/work/archi.png',
    href: 'https://github.com/archi-physics/archi'
  },
  {
    title: 'CloudCanvasAI',
    subtitle: 'Side project · 2025',
    body:
      'AI document creation platform pairing Claude with a live .docx / .pptx / .pdf / .xlsx preview and per-user E2B sandboxes.',
    image: '/work/cloudcanvas.png'
  },
  {
    title: 'ArgRAG',
    subtitle: 'Argusa AI Challenge 2025 · EPFL · Runner-up',
    body:
      'Enterprise RAG system built in 24 hours for multi-modal analysis, metadata intelligence, and real-time corporate document search.',
    image: '/work/argrag.png'
  }
];

const awards = [
  '2024 CMS Award for maintaining and innovating the unified CMS software base.',
  'Runner-up · Argusa AI Challenge 2025 at EPFL.',
  '2nd Prize · GovTech Hackathon 2021 (World Bank + Code for Pakistan).',
  'Top 10 finalist · CERN Webfest 2021.',
  'Certified Kubernetes Application Developer (CKAD).',
  'Machine Learning by Andrew Ng (Coursera); GCP Big Data & ML Fundamentals.'
];

const techStack = [
  {
    label: 'AI & LLM',
    items: [
      'LLM agents (Claude / OpenAI / Gemini)',
      'RAG',
      'MCP servers',
      'ReAct',
      'ChromaDB',
      'SentenceTransformers',
      'PyTorch',
      'TensorFlow'
    ]
  },
  {
    label: 'Languages',
    items: [
      'Python',
      'TypeScript',
      'JavaScript',
      'C / C++',
      'R',
      'Java',
      'SQL',
      'GraphQL'
    ]
  },
  {
    label: 'Frameworks',
    items: ['FastAPI', 'Flask', 'Django', 'Node.js', 'React', 'Angular', 'PyQt']
  },
  {
    label: 'Data',
    items: ['OracleDB', 'PostgreSQL', 'MongoDB', 'MySQL', 'OpenSearch', 'Airflow']
  },
  {
    label: 'DevOps',
    items: [
      'Kubernetes',
      'ArgoCD',
      'Kargo',
      'Docker',
      'GitHub Actions',
      'GitLab CI',
      'Terraform',
      'Ansible',
      'Puppet',
      'Grafana'
    ]
  },
  {
    label: 'Cloud',
    items: ['AWS', 'Azure', 'GCP', 'OpenStack', 'OpenShift']
  }
];

const AboutPage = ({ data }, location) => {
  const siteTitle = data.site.siteMetadata.title;

  return (
    <Layout title={siteTitle}>
      <SEO
        title="About"
        description="About Muhammad Hassan Ahmed (Hassan Ahmed): Inference Platform Engineer at Lyceum in Zurich, previously a software engineer at CERN on the CMS experiment and a research engineer at Retrocausal. BS Computer Science, FAST-NUCES."
        keywords={[`resume`, `Lyceum`, `inference`, `CERN`, `workflow`, `ml`]}
        schema={[
          {
            '@type': 'ProfilePage',
            '@id': `${siteUrl}/about/#profile`,
            url: `${siteUrl}/about/`,
            name: `About ${site.fullName}`,
            isPartOf: { '@id': websiteId },
            mainEntity: { '@id': personId }
          },
          // The publications listed below, each tied to the same Person.
          ...publications.map(pub => ({
            '@type': pub.type,
            '@id': pub.href,
            name: pub.label,
            url: pub.href,
            datePublished: pub.year,
            ...(pub.type === 'ScholarlyArticle' && {
              isPartOf: { '@type': 'CreativeWork', name: pub.venue }
            }),
            author: { '@id': personId }
          }))
        ]}
      />

      <article className="about-page">
        {/* Hero */}
        <header className="about-hero">
          <span className="about-kicker">About · Hassan Ahmed</span>
          <h1 className="about-title">
            AI engineer &amp; <em>full-stack</em> builder.
            <br />
            Shipping at Lyceum, building on the side.
          </h1>
          <p className="about-lead">
            I&apos;m Muhammad Hassan Ahmed, and I build LLM agents, RAG systems, and
            production ML end-to-end. I&apos;m an
            Inference Platform Engineer at <strong>Lyceum</strong> in Zurich, working
            on dedicated inference for open models. Before that, at CERN, I shipped
            the workflow-management stack that keeps CMS physics production running,
            plus <strong>Archi</strong>, an open-source RAG copilot for computing
            operations. On the side I build smaller LLM projects —{' '}
            <strong>CloudCanvasAI</strong>, <strong>Gemini Alchemy</strong>,{' '}
            <strong>LLM DevMate</strong> — that let me experiment with agent tooling,
            sandboxing, and model-ergonomics ideas.
          </p>
          <p className="about-meta">
            <span>{site.location}</span>
            <span className="about-dot" aria-hidden="true" />
            <span>{site.availability}</span>
          </p>
          <div className="about-cta-row">
            <MailtoLink
              className="about-cta about-cta-primary"
              encoded={site.emailEncoded}
            >
              Email me
            </MailtoLink>
            <a
              className="about-cta"
              href={site.linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn →
            </a>
            <a
              className="about-cta"
              href={site.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub →
            </a>
            {site.resume && (
              <a
                className="about-cta"
                href={site.resume}
                target="_blank"
                rel="noopener noreferrer"
              >
                CV ↓
              </a>
            )}
          </div>
        </header>

        {/* Metric strip */}
        <section className="about-metrics" aria-label="Highlights">
          {metrics.map(m => (
            <div key={m.label} className="metric-box">
              <div className="metric-val">{m.val}</div>
              <div className="metric-label">{m.label}</div>
            </div>
          ))}
        </section>

        {/* Timeline */}
        <section className="about-section">
          <div className="about-section-head">
            <span className="about-section-num">01</span>
            <h2 className="about-section-title">
              Career <em>timeline</em>
            </h2>
          </div>
          <div className="about-timeline">
            {experiences.map((exp, i) => (
              <div key={exp.role + exp.date} className="tl-item">
                <div className="tl-date">{exp.date}</div>
                <div className="tl-dot" data-idx={i % 4} />
                <div className="tl-content">
                  <div className="tl-title">{exp.role}</div>
                  <div className="tl-org">{exp.org}</div>
                  <p className="tl-sub">{exp.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Selected work with screenshots */}
        <section className="about-section">
          <div className="about-section-head">
            <span className="about-section-num">02</span>
            <h2 className="about-section-title">
              Selected <em>work</em>
            </h2>
          </div>
          <div className="about-work-grid">
            {selectedWork.map(w => {
              const card = (
                <figure key={w.title} className="work-card">
                  <div
                    className="work-card-image"
                    style={{ backgroundImage: `url(${w.image})` }}
                    role="img"
                    aria-label={w.title}
                  />
                  <figcaption className="work-card-body">
                    <div className="work-card-subtitle">{w.subtitle}</div>
                    <h3 className="work-card-title">{w.title}</h3>
                    <p className="work-card-description">{w.body}</p>
                  </figcaption>
                </figure>
              );
              return w.href ? (
                <a
                  key={w.title}
                  href={w.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="work-card-link"
                >
                  {card}
                </a>
              ) : (
                card
              );
            })}
          </div>
        </section>

        {/* Publications */}
        <section className="about-section">
          <div className="about-section-head">
            <span className="about-section-num">03</span>
            <h2 className="about-section-title">
              Selected <em>publications</em>
            </h2>
          </div>
          <ul className="about-pubs">
            {publications.map(pub => (
              <li key={pub.href} className="pub-item">
                <a href={pub.href} target="_blank" rel="noreferrer">
                  <span className="pub-venue">{pub.venue}</span>
                  <span className="pub-title">{pub.label}</span>
                  <span className="pub-arrow" aria-hidden="true">
                    ↗
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Awards + hackathons */}
        <section className="about-section">
          <div className="about-section-head">
            <span className="about-section-num">04</span>
            <h2 className="about-section-title">
              Awards &amp; <em>hackathons</em>
            </h2>
          </div>
          <ul className="about-awards">
            {awards.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Tech stack */}
        <section className="about-section" id="tech-stack">
          <div className="about-section-head">
            <span className="about-section-num">05</span>
            <h2 className="about-section-title">
              Tech <em>stack</em>
            </h2>
          </div>
          <div className="tech-stack-grid">
            {techStack.map(group => (
              <div key={group.label} className="tech-stack-group">
                <div className="tech-stack-label">{group.label}</div>
                <div className="tech-pill-row">
                  {group.items.map(item => (
                    <span key={item} className="tech-pill">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="about-section" id="contact">
          <div className="about-section-head">
            <span className="about-section-num">06</span>
            <h2 className="about-section-title">
              Let&rsquo;s <em>talk</em>
            </h2>
          </div>
          <p className="about-contact-lead">
            Fastest way to reach me is by email. I reply on LinkedIn too, and
            I&rsquo;m always up for notes on the open-source work.
          </p>
          <div className="about-contact-grid">
            <MailtoLink className="contact-card" encoded={site.emailEncoded}>
              <span className="contact-card-label">Email</span>
              <span className="contact-card-value">Click to send</span>
            </MailtoLink>
            <a
              className="contact-card"
              href={site.linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="contact-card-label">LinkedIn</span>
              <span className="contact-card-value">linkedin.com/in/hassan11196</span>
            </a>
            <a
              className="contact-card"
              href={site.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="contact-card-label">GitHub</span>
              <span className="contact-card-value">github.com/hassan11196</span>
            </a>
            <a
              className="contact-card"
              href={site.kaggle}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="contact-card-label">Kaggle</span>
              <span className="contact-card-value">kaggle.com/hassan11196</span>
            </a>
            <a
              className="contact-card"
              href={site.cern_gitlab}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="contact-card-label">CERN GitLab</span>
              <span className="contact-card-value">gitlab.cern.ch/ahmedmu</span>
            </a>
            {site.resume && (
              <a
                className="contact-card"
                href={site.resume}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="contact-card-label">CV · PDF</span>
                <span className="contact-card-value">
                  Dates, awards, and the full stack
                </span>
              </a>
            )}
          </div>
        </section>
      </article>
    </Layout>
  );
};

const indexQuery = graphql`
  query {
    site {
      siteMetadata {
        title
      }
    }
  }
`;

export default props => (
  <StaticQuery
    query={indexQuery}
    render={data => <AboutPage location={props.location} data={data} {...props} />}
  />
);
